/**
 * CatalogUploadStep - Step 1 of SMB Vendor Cost Import
 *
 * Upload SMB catalog file (Square or QuickBooks export)
 * Shows detected columns after successful upload
 */

import React, { useState, useRef } from 'react'
import { Space, Card, Alert, Button, Upload, Typography, List, Spin, Progress } from 'antd'
import { InboxOutlined, CheckCircleOutlined, FileExcelOutlined, LoadingOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { smbSyncService } from '../../../services/smbSyncService'
import { errorService } from '../../../services/errorService'
import { fileValidationService } from '../../../services/fileValidationService'
import type { StepProps } from './types'

const { Dragger } = Upload
const { Title, Text } = Typography

type UploadStatus = 'idle' | 'uploading' | 'processing' | 'success' | 'error'

/**
 * Step 1: Upload SMB catalog file
 */
export const CatalogUploadStep: React.FC<StepProps> = ({ state, onNext, onCancel }) => {
  const platform = state.platform
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle')
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [detectedColumns, setDetectedColumns] = useState<string[]>([])
  const [jobId, setJobId] = useState<string>('')
  const [progress, setProgress] = useState(0)

  // Ref to prevent double uploads (synchronous check, not subject to React batch updates)
  const isUploadingRef = useRef(false)

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options
    const uploadFile = file as File
    const fileName = uploadFile.name

    // Prevent double uploads using ref (synchronous check)
    if (isUploadingRef.current) {
      console.warn('Upload already in progress, ignoring duplicate request')
      return
    }
    isUploadingRef.current = true

    setUploadedFileName(fileName)
    setUploadStatus('uploading')
    setProgress(10)

    try {
      // Validate file extension
      const fileExtension = fileName.split('.').pop()?.toLowerCase()
      const allowedExtensions = ['csv', 'xlsx', 'xls']
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        // Use friendly file validation service instead of technical error
        setUploadStatus('error')
        setProgress(0)
        isUploadingRef.current = false
        fileValidationService.showInvalidExtension(fileName, allowedExtensions, handleRetry)
        onError?.(new Error('Invalid file extension'))
        return
      }

      // Step 1: Get presigned URL
      setProgress(20)
      const presignResponse = await smbSyncService.uploadCatalog(fileName, platform)

      if (!presignResponse.success || !presignResponse.data) {
        setUploadStatus('error')
        setProgress(0)
        isUploadingRef.current = false
        const errorMsg = presignResponse.message || 'Failed to get upload authorization'
        errorService.showError('Authorization Failed', errorMsg, presignResponse.errors)
        onError?.(new Error(errorMsg))
        return
      }

      const { job_id, put_url } = presignResponse.data
      setJobId(job_id)

      // Step 2: Upload to S3
      setProgress(40)
      const uploadResponse = await smbSyncService.uploadToS3(put_url, uploadFile)

      if (!uploadResponse.success) {
        setUploadStatus('error')
        setProgress(0)
        isUploadingRef.current = false
        const errorMsg = uploadResponse.message || 'Failed to upload file to S3'
        errorService.showError('S3 Upload Failed', errorMsg, uploadResponse.errors)
        onError?.(new Error(errorMsg))
        return
      }

      // Step 3: Poll for catalog processing to complete (S3 trigger handles processing)
      setUploadStatus('processing')
      setProgress(60)

      const pollResponse = await smbSyncService.pollUntilCatalogReady(job_id)

      if (!pollResponse.success || !pollResponse.data) {
        setUploadStatus('error')
        setProgress(0)
        isUploadingRef.current = false
        const errorMsg = pollResponse.message || 'Failed to process catalog file'

        // Check if this is a file content issue (user's file problem) vs system error
        // Check both error codes AND message content (backend may include code in message)
        const isFileIssue = pollResponse.errors?.some(
          (e) =>
            e.code === 'FORMAT_MISMATCH' ||
            e.code === 'INVALID_FORMAT' ||
            e.code === 'MISSING_COLUMNS' ||
            e.code === 'EMPTY_FILE' ||
            e.code === 'CORRUPTED_FILE' ||
            e.code === 'CSV_EMPTY' ||
            e.code === 'CSV_TOO_FEW_COLUMNS' ||
            e.code === 'CSV_NO_HEADER_ROW' ||
            e.code === 'CSV_DUPLICATE_COLUMNS' ||
            e.message?.includes('FORMAT_MISMATCH') ||
            e.message?.includes('CSV_')
        ) || errorMsg.includes('FORMAT_MISMATCH') || errorMsg.includes('format issue') || errorMsg.includes('CSV_')

        if (isFileIssue) {
          // Use friendly file validation service for user file issues
          // Note: Backend message already contains actionable fix instructions
          fileValidationService.showProcessingIssue(
            fileName,
            errorMsg,
            undefined,
            handleRetry
          )
        } else {
          // Use technical error service for system errors
          errorService.showError('Catalog Processing Failed', errorMsg, pollResponse.errors)
        }

        onError?.(new Error(errorMsg))
        return
      }

      // Success!
      const catalogColumns = pollResponse.data.catalog_columns || []
      setProgress(100)
      setUploadStatus('success')
      setDetectedColumns(catalogColumns)
      isUploadingRef.current = false
      onSuccess?.('ok')

    } catch (err: any) {
      setUploadStatus('error')
      setProgress(0)
      isUploadingRef.current = false
      const errorMsg = err.message || 'Upload failed'
      errorService.showError('Upload Failed', errorMsg)
      onError?.(err)
    }
  }

  const handleNext = () => {
    if (uploadStatus !== 'success') {
      errorService.showError(
        'Upload Required',
        'Please upload a catalog file before continuing',
        [{ code: 'NO_FILE_UPLOADED', message: 'Catalog file must be uploaded first' }]
      )
      return
    }

    onNext({
      jobId,
      platform,
      catalogFile: { name: uploadedFileName } as File,
      catalogColumns: detectedColumns,
    })
  }

  const handleRetry = () => {
    setUploadStatus('idle')
    setUploadedFileName('')
    setDetectedColumns([])
    setProgress(0)
    isUploadingRef.current = false
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Instructions */}
      <Card>
        <Title level={4}>Step 1: Upload Your Catalog</Title>
        <Text>
          Upload your product catalog exported from Square or QuickBooks. This file should contain
          your current product information including SKUs, prices, and costs.
        </Text>
      </Card>

      {/* Upload Zone */}
      <Card>
        {(uploadStatus === 'idle' || uploadStatus === 'error') && (
          <Dragger
            customRequest={handleUpload}
            accept=".csv,.xlsx,.xls"
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Drop your catalog file here</p>
            <p className="ant-upload-hint">or click to browse (CSV or Excel)</p>
          </Dragger>
        )}

        {(uploadStatus === 'uploading' || uploadStatus === 'processing') && (
          <Card style={{ textAlign: 'center', padding: '24px' }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: '32px', color: '#1890ff' }} />}
              style={{ marginBottom: '16px' }}
            />
            <div style={{ marginBottom: '16px' }}>
              <Text strong>
                {uploadStatus === 'uploading' ? 'Uploading file...' : 'Processing catalog data...'}
              </Text>
              <br />
              <Text type="secondary">File: {uploadedFileName}</Text>
            </div>
            <Progress
              percent={progress}
              status="active"
              strokeColor="#52c41a"
              style={{ maxWidth: '300px', margin: '0 auto' }}
            />
            {uploadStatus === 'processing' && (
              <div style={{ marginTop: '16px' }}>
                <Text type="secondary">
                  Detecting headers and extracting columns...
                </Text>
              </div>
            )}
          </Card>
        )}

        {uploadStatus === 'success' && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Catalog uploaded successfully!"
              description={`File: ${uploadedFileName}`}
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
            />

            {/* Show detected columns */}
            {detectedColumns.length > 0 && (
              <div>
                <Text strong>Detected Columns ({detectedColumns.length}):</Text>
                <List
                  size="small"
                  bordered
                  dataSource={detectedColumns}
                  style={{ marginTop: '8px', maxHeight: '200px', overflow: 'auto' }}
                  renderItem={(column) => (
                    <List.Item>
                      <Space>
                        <FileExcelOutlined />
                        <Text>{column}</Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </Space>
        )}

        {uploadStatus === 'error' && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Button onClick={handleRetry}>Try Again</Button>
          </div>
        )}
      </Card>

      {/* Action buttons */}
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button size="large" onClick={onCancel} disabled={uploadStatus === 'uploading' || uploadStatus === 'processing'}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            disabled={uploadStatus !== 'success'}
          >
            Next: Upload Vendor File
          </Button>
        </Space>
      </Card>
    </Space>
  )
}
